import { StandardEvents } from '@shopify/events';

/**
 * Amada ring-size gate.
 *
 * Dual mode:
 *   1. Property mode — `[data-amada-ring-size]` present (Style Family configurator).
 *      Requires a chosen value, gates ATC, syncs `properties[Ring Size]` into the product form.
 *   2. Native Size fieldset — legacy variant-picker Size / Ring Size options.
 *   3. Otherwise no-op (non-ring products).
 *
 * Catalogue constraints this is built around (see research/existing-site.md §3):
 *   - Option names are inconsistent: "Size", "Ring Size", "Ring size". Matching is
 *     therefore pattern based, never an exact name.
 *   - Most catalogue rings do not carry a native size option. The theme renders
 *     `[data-amada-ring-size]` controls for those products; non-rings remain untouched.
 *   - "Custom Size" / "Customize Size" are real purchasable variants, so they count as a
 *     valid choice and resolve the gate like any lettered size.
 *   - "I don't know" is a valid line-item property. A Custom Size variant is used when
 *     present; legacy native-size products fall back to an available variant ID while
 *     preserving the explicit unknown property for the fulfilment team.
 *
 * Theme contract:
 *   - The variant picker / configurator is NOT nested inside <product-form-component>, so
 *     controls and the button are located from a shared section root.
 *   - The theme dispatches StandardEvents.productSelect (@shopify/events) and re-renders
 *     via morph. It does NOT dispatch any 'variant:update' event.
 *   - Only the label span is rewritten. Writing button.textContent would destroy the
 *     icon-add-to-cart SVG and the .add-to-cart__added checkmark-burst markup, killing the
 *     add-to-cart success animation and fly-to-cart.
 */

const SIZE_PATTERN = /ring\s*size|^\s*size\s*$/i;
const CUSTOM_SIZE_PATTERN = /custom\s*size|customize\s*size/i;
const UNKNOWN_VALUE_PATTERN = /unknown|don'?t\s*know|i\s*don'?t\s*know/i;

class AmadaRingSizeGate extends HTMLElement {
  #root = null;
  #chooseLabel = 'Choose a ring size';
  #addLabel = 'Add to Bag';
  #unknownLabel = "I don't know";
  #unknownHelpText = 'Our expert team will contact you after your order to help confirm the right size.';
  #fallbackUrl = '/pages/inquire';
  #bookLabel = 'Book appointment';
  #bookUrl = 'https://calendly.com/amadadiamonds/30min';
  #onChange = null;
  #onProductSelect = null;
  #onStickyClick = null;
  #onPageShow = null;
  #unknownControl = null;
  #createdUnknownControl = false;
  #createdUnknownHelp = null;
  #bookControl = null;
  #forcedPropertySize = '';
  /** True while Fabric productSelect is in flight — ATC must stay disabled. */
  #selectPending = false;
  /** Native products without an Unknown variant still need an explicit line property. */
  #nativeUnknown = false;
  #selectingUnknown = false;
  /** @type {'property' | 'native' | 'none'} */
  #mode = 'none';

  connectedCallback() {
    this.#chooseLabel = this.dataset.chooseLabel || this.#chooseLabel;
    this.#addLabel = this.dataset.addLabel || this.#addLabel;
    this.#unknownLabel = this.dataset.unknownLabel || this.#unknownLabel;
    this.#unknownHelpText = this.dataset.unknownHelp || this.#unknownHelpText;
    this.#fallbackUrl = this.dataset.unknownFallbackUrl || this.#fallbackUrl;
    this.#bookLabel = this.dataset.bookLabel || this.#bookLabel;
    this.#bookUrl = this.dataset.bookUrl || this.#bookUrl;

    // Do not rely on rAF alone — hidden tabs never fire it.
    this.#init();
  }

  disconnectedCallback() {
    if (this.#onStickyClick) {
      this.ownerDocument.removeEventListener('click', this.#onStickyClick, true);
      this.#onStickyClick = null;
    }
    if (this.#onPageShow) {
      window.removeEventListener('pageshow', this.#onPageShow);
      this.#onPageShow = null;
    }
    for (const button of this.#buttons()) button.removeAttribute('data-amada-needs-size');
    if (this.#onChange) this.#root?.removeEventListener('change', this.#onChange);
    if (this.#onChange) this.#root?.removeEventListener('input', this.#onChange);
    if (this.#onProductSelect) {
      this.#root?.removeEventListener(StandardEvents.productSelect, this.#onProductSelect);
    }
    if (this.#createdUnknownControl) this.#unknownControl?.remove();
    this.#createdUnknownHelp?.remove();
    this.#bookControl?.remove();
  }

  #init() {
    const root =
      this.closest('quick-add-dialog') ||
      this.closest('dialog') ||
      this.closest('.shopify-section, [id^="shopify-section-"]');
    if (!root) return;
    this.#root = root;

    if (!this.#buttons().length) {
      this.#mountBookLink();
      return;
    }

    if (root.querySelector('[data-amada-ring-size]')) {
    this.#mode = 'property';
    this.#restoreOrClearPropertySize();
    if (!this.#onPageShow) {
      this.#onPageShow = () => {
        this.#restoreOrClearPropertySize();
        this.#sync();
      };
      window.addEventListener('pageshow', this.#onPageShow);
    }
      this.#mountUnknownSizeControl();
      this.#mountChangeControl();
      this.#mountBookLink();
      this.#bindGuide();
      this.#sync();
      this.#bindListeners();
      return;
    }

    if (!root.querySelector('variant-picker')) {
      this.#mode = 'none';
      this.#mountBookLink();
      return;
    }

    // No size dimension on this product: leave the button exactly as the theme rendered it.
    if (!this.#sizeFieldset()) {
      this.#mode = 'none';
      this.#mountBookLink();
      return;
    }

    this.#mode = 'native';
    this.#clearPreselectedSize();
    this.#syncSizeIntoForm('');
    this.#mountUnknownSizeControl();
    this.#mountBookLink();
    this.#bindGuide();
    this.#sync();
    this.#bindListeners();
  }

  #bindListeners() {
    const root = this.#root;
    if (!root) return;

    // Capture on the document, because the sticky button's own `on:click` is
    // itself a delegated document listener (component.js). Capture runs before
    // the event reaches the target, so this is the only place the forward to a
    // disabled button can be intercepted.
    if (!this.#onStickyClick && !this.#isDialogRoot()) {
      this.#onStickyClick = (event) => {
        const button = event.target?.closest?.(
          '.sticky-add-to-cart__bar button.add-to-cart-button, .sticky-add-to-cart__bar .add-to-cart-button button, .sticky-add-to-cart__bar button[name="add"], button[data-amada-needs-size], .quick-add-buy-now-btn[aria-disabled="true"]'
        );
        if (!button || !this.ownerDocument.contains(button)) return;
        if (!this.#buttons().includes(button) && !this.#isStickyButton(button)) return;

        // Only intercept when size is still required.
        if (this.#sizeChosen() && !this.#selectPending) return;

        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation?.();
        this.#revealSizeControl();
      };
      this.ownerDocument.addEventListener('click', this.#onStickyClick, true);
    }

    // Delegated to the section root: morph can replace picker/configurator nodes.
    this.#onChange = (event) => {
      if (event.target instanceof Element && event.target.closest('[data-amada-personalise]')) {
        this.#sync();
        return;
      }
      if (
        this.#mode === 'property' &&
        event.target instanceof HTMLSelectElement &&
        event.target.matches('[data-amada-ring-size]')
      ) {
        for (const input of this.#propertyControls()) {
          if (input instanceof HTMLInputElement && UNKNOWN_VALUE_PATTERN.test(input.value || '')) {
            input.checked = false;
          }
        }
      }
      if (
        this.#mode === 'property' &&
        event.target instanceof Element &&
        event.target.matches('[data-amada-ring-size]')
      ) {
        const next =
          event.target instanceof HTMLSelectElement
            ? event.target.value
            : event.target instanceof HTMLInputElement && event.target.checked
              ? event.target.value
              : '';
        this.#forcedPropertySize = UNKNOWN_VALUE_PATTERN.test(next || '') ? '' : next;
        this.#rememberSize(this.#forcedPropertySize);
      }
      if (
        this.#mode === 'native' &&
        !this.#selectingUnknown &&
        this.#sizeFieldset()?.contains(event.target)
      ) {
        this.#nativeUnknown = false;
        this.#unknownControl?.setAttribute('aria-pressed', 'false');
      }
      this.#mountUnknownSizeControl();
      this.#syncSizeIntoForm();
      this.#sync();
    };
    root.addEventListener('change', this.#onChange);
    root.addEventListener('input', this.#onChange);

    this.#onProductSelect = (event) => {
      const promise = event?.promise;
      if (!promise) {
        this.#mountUnknownSizeControl();
        this.#mountChangeControl();
        if (this.#nativeUnknown) this.#paintNativeUnknownState();
        this.#syncSizeIntoForm();
        this.#sync();
        return;
      }
      // Fabric may queue ATC against a stale form snapshot. Keep Add disabled for the
      // whole resolve, then restore Ring Size into the live form before re-enabling.
      this.#selectPending = true;
      this.#sync();
      promise
        .then(() => {
          this.#mountUnknownSizeControl();
          this.#mountChangeControl();
          if (this.#nativeUnknown) this.#paintNativeUnknownState();
          this.#syncSizeIntoForm();
          this.#selectPending = false;
          this.#sync();
        })
        .catch((error) => {
          this.#selectPending = false;
          this.#syncSizeIntoForm();
          this.#sync();
          if (error?.name !== 'AbortError') {
            console.warn('[amada-ring-size-gate] product select rejected:', error);
          }
        });
    };
    root.addEventListener(StandardEvents.productSelect, this.#onProductSelect);
  }

  /** Both the main buy button and the sticky bar's button gate together. */
  /**
   * Every Add-to-Cart the gate is responsible for, main buy box and sticky bar.
   *
   * The sticky bar's button carries `add-to-cart-button` on the button itself
   * and has no `name="add"` — it is `type="button"` driven by an `on:click`
   * handler. The two descendant-shaped selectors below matched nothing there,
   * so the gate never saw it: the main button sat disabled reading "Choose a
   * ring size" while the sticky button stayed enabled reading "Add to cart",
   * and clicking it forwarded to a disabled button, which the browser discards
   * in silence. `button.add-to-cart-button` is the one that matches.
   */
  #isDialogRoot() {
    return Boolean(this.#root?.closest?.('quick-add-dialog, dialog'));
  }

  #buttons() {
    const local = [
      ...(this.#root?.querySelectorAll(
        '.add-to-cart-button button, button[name="add"], .quick-add-buy-now-btn'
      ) ?? []),
    ];
    if (this.#isDialogRoot()) {
      return local.filter((el, i, all) => all.indexOf(el) === i);
    }

    return [
      ...local,
      ...this.ownerDocument.querySelectorAll(
        '.sticky-add-to-cart__bar button.add-to-cart-button, .sticky-add-to-cart__bar .add-to-cart-button button, .sticky-add-to-cart__bar button[name="add"]'
      ),
    ].filter((el, i, all) => all.indexOf(el) === i);
  }

  #isStickyButton(button) {
    return Boolean(button.closest?.('.sticky-add-to-cart__bar'));
  }

  /**
   * Carry the shopper to the size control instead of stopping them at it.
   *
   * The sticky bar is on screen precisely when the buy box is not, so a
   * disabled button there names the blocker without offering a way past it.
   * The button stays live and this runs in its place.
   */
  #revealSizeControl() {
    // Both gate modes have to be handled here. Most catalogue rings carry no
    // native size option, so they run in `property` mode where the control is
    // `[data-amada-ring-size]` and `#sizeFieldset()` is null.

    // If compact summary is showing "I don't know", open the editor first.
    const changeBtn = this.#root?.querySelector('[data-amada-ring-size-change]');
    const summary = this.#root?.querySelector('[data-amada-ring-size-summary]');
    if (changeBtn instanceof HTMLElement && summary instanceof HTMLElement && !summary.hidden) {
      changeBtn.click();
    }

    const fieldset =
      this.#root?.querySelector('[data-amada-selector="ring_size"]') ||
      this.#sizeFieldset() ||
      this.#root?.querySelector('.amada-ring-size-property, [data-amada-ring-size]')?.closest('fieldset, .amada-ring-size-property, div');

    if (fieldset instanceof HTMLElement) fieldset.hidden = false;

    const control =
      this.#mode === 'property'
        ? this.#propertyControls().find((el) => el instanceof HTMLSelectElement || (el instanceof HTMLInputElement && !el.disabled)) ??
          this.#root?.querySelector('select[data-amada-ring-size], [data-amada-ring-size]') ??
          null
        : this.#sizeFieldset()?.querySelector(
            'select, input[type="radio"]:not([disabled]), input[type="checkbox"]:not([disabled])'
          ) ?? null;

    const scrollTarget =
      (control instanceof HTMLElement && (control.closest('fieldset, .amada-ring-size-property, [data-amada-selector]') || control)) ||
      fieldset ||
      this.#sizeFieldset();
    if (!scrollTarget) return;

    const view = this.ownerDocument.defaultView;
    const reduced = view?.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
    scrollTarget.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'center' });

    // Scroll only — do not open the ring-size dropdown/picker.
  }

  #productForm() {
    const fromButton = this.#buttons()[0]?.closest('form');
    if (fromButton) return fromButton;

    return (
      this.#root?.querySelector('form[data-type="add-to-cart-form"]') ||
      this.#root?.querySelector('.buy-buttons-block form[action*="/cart/add"]') ||
      [...(this.#root?.querySelectorAll('form[action*="/cart/add"]') ?? [])].find(
        (form) => !/installment/i.test(form.id || '')
      ) ||
      null
    );
  }

  #sizeMemoryKey() {
    return `amada-ring-size:${location.pathname}`;
  }

  #rememberSize(size) {
    // Quick-add shares the collection pathname across products — never persist there.
    if (this.#isDialogRoot()) return;
    try {
      const next = String(size || '').trim();
      if (!next || UNKNOWN_VALUE_PATTERN.test(next)) sessionStorage.removeItem(this.#sizeMemoryKey());
      else sessionStorage.setItem(this.#sizeMemoryKey(), next);
    } catch {
      /* private mode */
    }
  }

  #rememberedSize() {
    if (this.#isDialogRoot()) return '';
    try {
      return sessionStorage.getItem(this.#sizeMemoryKey()) || '';
    } catch {
      return '';
    }
  }

  #propertyControls() {
    return [...(this.#root?.querySelectorAll('[data-amada-ring-size]') ?? [])];
  }

  #sizeFieldset() {
    const scope = this.#root?.querySelector('variant-picker');
    if (!scope) return null;

    for (const fieldset of scope.querySelectorAll('fieldset')) {
      const legend = fieldset.querySelector('legend')?.textContent?.trim() || '';
      if (SIZE_PATTERN.test(legend)) return fieldset;
    }
    return null;
  }

  #customSizeControl() {
    if (this.#mode === 'property') {
      for (const input of this.#propertyControls()) {
        if (UNKNOWN_VALUE_PATTERN.test(input.value || '')) return input;
      }
      return null;
    }

    const fieldset = this.#sizeFieldset();
    if (!fieldset) return null;

    for (const input of fieldset.querySelectorAll('input[type="radio"], input[type="checkbox"]')) {
      const label =
        input.getAttribute('value') ||
        fieldset.querySelector(`label[for="${input.id}"]`)?.textContent ||
        input.closest('label')?.textContent ||
        '';
      if (CUSTOM_SIZE_PATTERN.test(label)) return input;
    }

    const select = fieldset.querySelector('select');
    if (select) {
      for (const option of select.options) {
        if (CUSTOM_SIZE_PATTERN.test(option.textContent || option.value || '')) {
          return { select, option };
        }
      }
    }
    return null;
  }

  #mountUnknownSizeControl() {
    if (this.#mode === 'property') {
      const fieldset = this.#root?.querySelector('[data-amada-selector="ring_size"]');
      if (!fieldset) return;
      if (this.#unknownControl?.isConnected) return;

      const existing = fieldset.querySelector('[data-amada-unknown-size-trigger]');
      const el = existing || document.createElement('button');
      if (!existing) {
        el.type = 'button';
        el.className = 'amada-unknown-size';
        el.textContent = this.#unknownLabel;
        fieldset.insertAdjacentElement('afterend', el);
        this.#createdUnknownControl = true;
      }
      if (!el.__amadaRingSizeBound) {
        el.addEventListener('click', (event) => {
          event.preventDefault();
          if (this.#openGuide()) return;
          this.#selectUnknownPropertySize();
        });
        el.__amadaRingSizeBound = true;
      }
      this.#unknownControl = el;
      this.#mountUnknownHelp();
      return;
    }

    const fieldset = this.#sizeFieldset();
    if (!fieldset) return;

    if (this.#unknownControl?.isConnected) return;

    const custom = this.#customSizeControl();
    const el = document.createElement('button');
    el.type = 'button';
    el.className = 'amada-unknown-size';
    el.textContent = this.#unknownLabel;
    el.addEventListener('click', (event) => {
      event.preventDefault();
      if (this.#openGuide()) return;
      const target = custom || this.#firstAvailableNativeControl();
      if (!target) {
        window.location.href = this.#fallbackUrl;
        return;
      }
      this.#selectCustomSize(target, true);
    });

    fieldset.insertAdjacentElement('afterend', el);
    this.#unknownControl = el;
    this.#createdUnknownControl = true;
    this.#mountUnknownHelp();
  }

  #mountChangeControl() {
    const change = this.#root?.querySelector('[data-amada-ring-size-change]');
    if (!change || change.__amadaRingSizeBound) return;

    change.addEventListener('click', () => {
      this.#forcedPropertySize = '';
      this.#rememberSize('');
      for (const control of this.#propertyControls()) {
        if (control instanceof HTMLInputElement) control.checked = false;
        if (control instanceof HTMLSelectElement) control.value = '';
      }
      this.#syncSizeIntoForm('');
      this.#sync();
      this.#root
        ?.querySelector('select[data-amada-ring-size], input[data-amada-ring-size]')
        ?.focus();
    });
    change.__amadaRingSizeBound = true;
  }

  #mountUnknownHelp() {
    let help = this.#root?.querySelector('[data-amada-unknown-size-help]');
    if (!help && this.#unknownControl) {
      help = document.createElement('p');
      help.className = 'amada-ring-size-property__unknown-help';
      help.dataset.amadaUnknownSizeHelp = '';
      help.textContent = this.#unknownHelpText;
      help.hidden = true;
      this.#unknownControl.insertAdjacentElement('afterend', help);
      this.#createdUnknownHelp = help;
    }
    if (help && !help.textContent.trim()) help.textContent = this.#unknownHelpText;
  }

  #guide() {
    return this.ownerDocument.querySelector('amada-ring-size-guide');
  }

  #openGuide() {
    if (this.#isDialogRoot()) return false;
    const guide = this.#guide();
    if (!guide) {
      console.warn(
        '[amada-ring-size-gate] Ring size guide missing. Ensure product-information has enable_ring_size_gate on and amada-ring-size-guide assets are uploaded.'
      );
      return false;
    }
    if (typeof guide.open !== 'function') {
      console.warn(
        '[amada-ring-size-gate] Ring size guide is not ready (open missing). Check Console for amada-ring-size-guide.js / @amada/ring-size-data load errors.'
      );
      return false;
    }
    guide.open();
    return true;
  }

  #bindGuide() {
    const guide = this.#guide();
    if (!guide || guide.__amadaRingSizeBound) return;
    guide.addEventListener('amada:ring-size-unknown', () => this.#applyUnknownFromGuide());
    guide.addEventListener('amada:ring-size-selected', (event) => {
      this.#applyUkSize(event.detail?.size || event.detail?.uk || '');
    });
    guide.__amadaRingSizeBound = true;
  }

  #applyUnknownFromGuide() {
    this.#forcedPropertySize = '';
    if (this.#mode === 'property') {
      this.#selectUnknownPropertySize();
      return;
    }
    const custom = this.#customSizeControl();
    const target = custom || this.#firstAvailableNativeControl();
    if (!target) return;
    this.#selectCustomSize(target, true);
  }

  #applyUkSize(uk) {
    const size = String(uk || '').trim();
    if (!size) return;
    this.#forcedPropertySize = size;

    if (this.#mode === 'property') {
      const controls = this.#propertyControls();
      let matched = null;
      for (const control of controls) {
        if (control instanceof HTMLSelectElement) {
          const option = this.#ensureSelectOption(control, size);
          if (option) {
            control.value = option.value;
            matched = control;
          } else {
            control.value = '';
          }
        } else if (control instanceof HTMLInputElement) {
          const isMatch = this.#sizeTokensMatch(control.value, size);
          control.checked = isMatch;
          if (isMatch) matched = control;
        }
      }
      this.#syncSizeIntoForm(size);
      matched?.dispatchEvent(new Event('change', { bubbles: true }));
      this.#sync();
      if (!matched) this.#paintSelectedSummary(size);
      else this.#paintOptionValue(size);
      return;
    }

    const fieldset = this.#sizeFieldset();
    if (fieldset) {
      let matched = false;
      for (const input of fieldset.querySelectorAll('input[type="radio"], input[type="checkbox"]')) {
        const label = (input.getAttribute('value') || '').trim();
        const isMatch = this.#sizeTokensMatch(label, size);
        input.checked = isMatch;
        if (isMatch) matched = true;
      }
      const select = fieldset.querySelector('select');
      if (select) {
        const option = [...select.options].find(
          (item) =>
            this.#sizeTokensMatch(item.value, size) || this.#sizeTokensMatch(item.textContent, size)
        );
        if (option) {
          select.value = option.value;
          matched = true;
        } else {
          // Native whole-letter variants: fall back to the whole letter when half is unavailable.
          // Do not inject half-size options into real variant selects.
          const whole = size.replace(/½/gi, '').replace(/\.5$/i, '').trim();
          const wholeOption = [...select.options].find(
            (item) =>
              this.#sizeTokensMatch(item.value, whole) || this.#sizeTokensMatch(item.textContent, whole)
          );
          if (wholeOption) {
            select.value = wholeOption.value;
            matched = true;
            this.#forcedPropertySize = wholeOption.value || whole;
          }
        }
      }
      if (matched) {
        fieldset.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    this.#nativeUnknown = false;
    this.#syncSizeIntoForm(this.#forcedPropertySize || size);
    this.#sync();
    this.#paintOptionValue(this.#forcedPropertySize || size);
  }

  #normaliseSizeToken(value) {
    return String(value ?? '')
      .trim()
      .toUpperCase()
      .replace(/\s+/g, '')
      .replace(/1\/2/g, '½')
      .replace(/\.5$/, '½');
  }

  #sizeTokensMatch(a, b) {
    const left = this.#normaliseSizeToken(a);
    const right = this.#normaliseSizeToken(b);
    return Boolean(left && right && left === right);
  }

  #ensureSelectOption(select, size) {
    if (!(select instanceof HTMLSelectElement) || !size) return null;
    const existing = [...select.options].find((item) => this.#sizeTokensMatch(item.value, size) || this.#sizeTokensMatch(item.textContent, size));
    if (existing) return existing;

    // Measured half sizes (I½) are valid line properties even when the catalogue
    // only lists whole letters. Inject a temporary option so the UI can show it.
    const option = document.createElement('option');
    option.value = size;
    option.textContent = size;
    option.dataset.amadaMeasuredSize = 'true';
    select.appendChild(option);
    return option;
  }

  #paintOptionValue(size) {
    const displayedValue = this.#root?.querySelector('[data-amada-option-value]');
    if (displayedValue) {
      displayedValue.textContent = size;
      displayedValue.hidden = false;
    }
  }

  #paintSelectedSummary(size) {
    this.#paintOptionValue(size);
    const summary = this.#root?.querySelector('[data-amada-ring-size-summary]');
    const value = summary?.querySelector('[data-amada-ring-size-summary-value]');
    if (value instanceof HTMLElement) value.textContent = size;
    if (summary instanceof HTMLElement) summary.hidden = false;
  }

  #selectUnknownPropertySize() {
    const controls = this.#propertyControls();
    let unknown = controls.find((input) => UNKNOWN_VALUE_PATTERN.test(input.value || ''));

    if (!unknown) {
      // No Unknown chip — hand off to booking/contact.
      window.location.href = this.#fallbackUrl;
      return;
    }

    for (const control of controls) {
      if (control instanceof HTMLSelectElement) control.value = '';
    }
    unknown.checked = true;
    unknown.dispatchEvent(new Event('change', { bubbles: true }));
    this.#syncSizeIntoForm(this.#unknownLabel);
    this.#sync();
  }

  #firstAvailableNativeControl() {
    const fieldset = this.#sizeFieldset();
    if (!fieldset) return null;

    const input = [...fieldset.querySelectorAll('input[type="radio"], input[type="checkbox"]')].find(
      (candidate) =>
        !candidate.disabled &&
        candidate.getAttribute('aria-disabled') !== 'true' &&
        candidate.dataset.optionAvailable !== 'false'
    );
    if (input) return input;

    const select = fieldset.querySelector('select');
    const option = select
      ? [...select.options].find((candidate) => !candidate.disabled && candidate.value)
      : null;
    return select && option ? { select, option } : null;
  }

  #selectCustomSize(custom, asUnknown = false) {
    this.#nativeUnknown = asUnknown;
    this.#selectingUnknown = asUnknown;

    if (custom.select && custom.option) {
      custom.select.value = custom.option.value;
      custom.select.dispatchEvent(new Event('change', { bubbles: true }));
      this.#selectingUnknown = false;
      this.#syncSizeIntoForm(asUnknown ? this.#unknownLabel : undefined);
      this.#sync();
      return;
    }

    const input = custom;
    if (input.disabled) return;
    input.checked = true;
    if ('currentChecked' in input.dataset) input.dataset.currentChecked = 'true';
    input.dispatchEvent(new Event('change', { bubbles: true }));
    input.dispatchEvent(new Event('input', { bubbles: true }));
    this.#selectingUnknown = false;
    this.#syncSizeIntoForm(asUnknown ? this.#unknownLabel : undefined);
    this.#sync();
  }

  #paintNativeUnknownState() {
    const fieldset = this.#sizeFieldset();
    if (!fieldset) return;

    for (const input of fieldset.querySelectorAll('input:checked')) {
      input.checked = false;
      if ('currentChecked' in input.dataset) input.dataset.currentChecked = 'false';
    }
    const displayedValue = fieldset.querySelector('[data-amada-option-value]');
    if (displayedValue) {
      displayedValue.textContent = this.#unknownLabel;
      displayedValue.hidden = false;
    }
    this.#unknownControl?.setAttribute('aria-pressed', 'true');
  }

  #mountBookLink() {
    if (this.#isDialogRoot()) return;
    if (!this.#bookLabel || !this.#bookUrl) return;
    if (this.#bookControl?.isConnected) return;

    const form =
      this.#root?.querySelector('product-form-component, .product-form, form[action*="/cart/add"]') ||
      this.#buttons()[0]?.closest('form, .buy-buttons, .product-details');
    if (!form) return;

    const link = document.createElement('a');
    link.href = this.#bookUrl;
    link.className = 'amada-book-secondary';
    link.textContent = this.#bookLabel;
    form.insertAdjacentElement('afterend', link);
    this.#bookControl = link;
  }

  /**
   * Property mode: restore ring_size from the URL when it matches a chip;
   * otherwise clear and keep ATC gated. Never blind-clear a valid deep link.
   */
  #restoreOrClearPropertySize() {
    const controls = this.#propertyControls();

    // Each quick-add product must start with no size selected.
    if (this.#isDialogRoot()) {
      this.#forcedPropertySize = '';
      for (const input of controls) {
        if (input instanceof HTMLInputElement) input.checked = false;
        if (input instanceof HTMLSelectElement) input.value = '';
      }
      this.#syncSizeIntoForm('');
      return;
    }

    const urlSize = new URLSearchParams(window.location.search).get('ring_size') || '';
    const match = urlSize
      ? controls.find(
          (input) => {
            if (input instanceof HTMLInputElement) {
              return String(input.value).trim().toLowerCase() === urlSize.trim().toLowerCase();
            }
            if (input instanceof HTMLSelectElement) {
              return [...input.options].some(
                (option) =>
                  String(option.value).trim().toLowerCase() === urlSize.trim().toLowerCase()
              );
            }
            return false;
          }
        )
      : null;

    if (match instanceof HTMLInputElement || match instanceof HTMLSelectElement) {
      for (const input of controls) {
        if (input instanceof HTMLInputElement) input.checked = input === match;
        if (input instanceof HTMLSelectElement) input.value = input === match ? urlSize : '';
      }
      this.#syncSizeIntoForm(urlSize);
      this.#rememberSize(urlSize);
      return;
    }

    const already = controls.find(
      (input) =>
        (input instanceof HTMLInputElement && input.checked && input.value && !UNKNOWN_VALUE_PATTERN.test(input.value)) ||
        (input instanceof HTMLSelectElement && input.value && !UNKNOWN_VALUE_PATTERN.test(input.value))
    );
    if (already instanceof HTMLInputElement || already instanceof HTMLSelectElement) {
      this.#forcedPropertySize = already.value;
      this.#syncSizeIntoForm(already.value);
      this.#rememberSize(already.value);
      return;
    }

    const remembered = this.#rememberedSize();
    if (remembered) {
      this.#applyUkSize(remembered);
      return;
    }

    for (const input of controls) {
      if (input instanceof HTMLInputElement) input.checked = false;
      if (input instanceof HTMLSelectElement) input.value = '';
    }
    this.#syncSizeIntoForm('');
  }

  /**
   * Nothing is preselected, so the shopper makes a deliberate choice.
   *
   * The hidden input[name="id"] is cleared directly so the form can never POST the
   * Liquid-rendered variant while the UI shows nothing selected.
   *
   * A `change` event is deliberately NOT dispatched here: <variant-picker> treats it as a
   * user selection and immediately re-resolves a variant, which re-checks a size and
   * defeats the clear. The picker rebuilds its own state from the DOM on the shopper's
   * first real click, and until then the disabled button is what guards the purchase.
   */
  #clearPreselectedSize() {
    const fieldset = this.#sizeFieldset();
    if (!fieldset) return;

    let cleared = false;
    for (const input of fieldset.querySelectorAll('input:checked')) {
      input.checked = false;
      if ('currentChecked' in input.dataset) input.dataset.currentChecked = 'false';
      cleared = true;
    }
    for (const select of fieldset.querySelectorAll('select')) {
      if (select.selectedIndex < 0) continue;
      select.selectedIndex = -1;
      cleared = true;
    }

    if (!cleared) return;

    const variantInput = this.#root?.querySelector('input[name="id"], [ref="variantId"]');
    if (variantInput) variantInput.value = '';
  }

  #syncSizeIntoForm(forcedValue) {
    const form = this.#productForm();
    if (!form) return;

    let value = forcedValue;
    if (value === undefined) {
      if (this.#forcedPropertySize) {
        value = this.#forcedPropertySize;
      } else if (this.#mode === 'property') {
        const selected = this.#propertyControls().find(
          (input) => input instanceof HTMLInputElement && input.checked
        );
        const select = this.#propertyControls().find(
          (input) => input instanceof HTMLSelectElement && input.value
        );
        value = selected?.value || select?.value || '';
      } else if (this.#nativeUnknown) {
        value = this.#unknownLabel;
      } else {
        const fieldset = this.#sizeFieldset();
        const checked = fieldset?.querySelector('input:checked');
        const select = fieldset?.querySelector('select');
        value = checked?.value || select?.value || '';
      }
    }
    if (UNKNOWN_VALUE_PATTERN.test(value || '')) value = this.#unknownLabel;

    let input = form.querySelector('input[name="properties[Ring Size]"]');
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'properties[Ring Size]';
      form.appendChild(input);
    }
    input.value = value || '';
  }

  #sizeChosen() {
    if (this.#forcedPropertySize) return true;
    if (this.#mode === 'property') {
      return this.#propertyControls().some(
        (input) =>
          (input instanceof HTMLInputElement && input.checked && input.value) ||
          (input instanceof HTMLSelectElement && input.value)
      );
    }

    const fieldset = this.#sizeFieldset();
    if (!fieldset) return true;
    if (this.#nativeUnknown) return true;

    if (fieldset.querySelector('input:checked')) return true;

    const select = fieldset.querySelector('select');
    return Boolean(select && select.selectedIndex >= 0 && select.value);
  }

  /**
   * The price element carries "Regular price …" / "Sale price …" labels, and on a
   * discounted product both amounts appear in order. Take the last currency amount, which
   * is the one actually being charged.
   */
  #price() {
    const el = this.#root?.querySelector('product-price .price, product-price, .price');
    const text = el?.textContent?.replace(/\s+/g, ' ') || '';
    const amounts = text.match(/[^\s]*\d[\d.,]*\s?[A-Z]{0,3}/g);
    if (!amounts?.length) return '';
    const price = amounts[amounts.length - 1].replace(/\s?[A-Z]{3}$/, '').trim();

    const host = this.#root?.querySelector('[data-amada-personalise]');
    const engravingOn =
      host?.dataset.engravingConfirmed === 'true' &&
      Boolean(String(host?.querySelector('input, textarea')?.value || '').trim());
    const feeCents = Number(host?.dataset.feeCents || 0);
    if (!engravingOn || !feeCents) return price;

    const cleaned = price.replace(/[^\d.,]/g, '');
    const value = Number(cleaned.replace(/,/g, ''));
    if (!Number.isFinite(value)) return price;
    const formatted = (value + feeCents / 100).toLocaleString('en-GB', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    const prefix = price.replace(/[\d.,\s]+/g, '').trim() || '£';
    return `${prefix}${formatted}`;
  }

  #syncCompactState(unknownChosen) {
    if (this.#mode !== 'property') return;

    const fieldset = this.#root?.querySelector('[data-amada-selector="ring_size"]');
    const summary = this.#root?.querySelector('[data-amada-ring-size-summary]');
    const value = summary?.querySelector('[data-amada-ring-size-summary-value]');
    if (fieldset instanceof HTMLElement) fieldset.hidden = unknownChosen;
    if (summary instanceof HTMLElement) summary.hidden = !unknownChosen;
    if (value instanceof HTMLElement && unknownChosen) value.textContent = this.#unknownLabel;
  }

  #sync() {
    if (this.#mode === 'none') return;

    const propertyUnknown =
      this.#mode === 'property' &&
      this.#propertyControls().some(
        (input) =>
          ((input instanceof HTMLInputElement && input.checked) ||
            input instanceof HTMLSelectElement) &&
          UNKNOWN_VALUE_PATTERN.test(input.value)
      );
    const unknownChosen = this.#nativeUnknown || propertyUnknown;
    const help = this.#root?.querySelector('[data-amada-unknown-size-help]');
    if (help instanceof HTMLElement) help.hidden = !unknownChosen;
    this.#unknownControl?.setAttribute('aria-pressed', unknownChosen ? 'true' : 'false');
    this.#syncCompactState(unknownChosen);

    const buttons = this.#buttons();
    if (!buttons.length) return;

    const chosen = this.#sizeChosen() && !this.#selectPending;

    for (const payment of this.#root?.querySelectorAll('[data-amada-size-payment]') ?? []) {
      payment.hidden = !chosen;
      payment.setAttribute('aria-hidden', chosen ? 'false' : 'true');
    }

    const price = chosen ? this.#price() : '';
    const label = this.#selectPending
      ? this.#addLabel
      : chosen
        ? price
          ? `${this.#addLabel} · ${price}`
          : this.#addLabel
        : this.#chooseLabel;

    for (const button of buttons) {
      if (!button.isConnected) continue;

      const isBuyNow = button.classList.contains('quick-add-buy-now-btn');
      if (isBuyNow) {
        // Keep label as "Buy now" always — never copy "Choose a ring size" from ATC.
        button.disabled = false;
        if (button.textContent.trim() !== 'Buy now' && button.getAttribute('aria-busy') !== 'true') {
          button.textContent = 'Buy now';
        }
        if (chosen) {
          button.removeAttribute('aria-disabled');
          button.removeAttribute('data-amada-needs-size');
        } else {
          button.setAttribute('aria-disabled', 'true');
          button.setAttribute('data-amada-needs-size', 'true');
        }
        continue;
      }

      const sticky = this.#isStickyButton(button);
      // Sticky bar must stay clickable so "Choose a ring size" can scroll/focus the control.
      // Main buy box stays truly disabled until a size is chosen.
      if (sticky) {
        button.disabled = false;
        if (chosen) {
          button.removeAttribute('aria-disabled');
          button.removeAttribute('data-amada-needs-size');
        } else {
          button.setAttribute('aria-disabled', 'true');
          button.setAttribute('data-amada-needs-size', 'true');
        }
      } else {
        button.disabled = !chosen;
        if (chosen) {
          button.removeAttribute('aria-disabled');
          button.removeAttribute('data-amada-needs-size');
        } else {
          button.setAttribute('aria-disabled', 'true');
          button.setAttribute('data-amada-needs-size', 'true');
        }
      }

      const labelEl = button.querySelector('[data-add-to-cart-label]');
      if (labelEl) {
        if (labelEl.textContent.trim() !== label) labelEl.textContent = label;
      } else if (button.textContent && button.textContent.trim() !== label && !button.querySelector('svg')) {
        // Prefer label element; only rewrite plain text buttons (never Buy now).
        if (sticky) {
          button.textContent = label;
        }
      }
    }
  }
}

if (!customElements.get('amada-ring-size-gate')) {
  customElements.define('amada-ring-size-gate', AmadaRingSizeGate);
}
